import { makeAutoObservable, toJS } from "mobx"
import intersection from "lodash/intersection"

import type { AppRestrictions } from "@framework/types/app"
import RootStore from "@store/RootStore"
import config from "@root/config"

import { isCopilotStudioEnabled } from "@framework/constants/copilot-studio"

import { dataSourcesRestrictions } from "./constants"
import { extractSourceTypeBlackList } from "./utils"

// turn true to cancel all restrictions
const isFullAccess = config.FULL_ACCESS_MODE
const isALLFeaturesAvailable = config.ALL_FEATURES_ENABLED || isFullAccess
const isAllActionsAvailable = config.ALL_ACTIONS_ENABLED || isFullAccess

export class RestrictionsStore {
  rootStore: RootStore

  constructor(rootStore: RootStore) {
    makeAutoObservable(this)
    this.rootStore = rootStore
  }

  get userActions() {
    if (this.rootStore.userStore.userActions == null) return null
    return new Set(
      Object.values(this.rootStore.userStore.userActions).map((it) =>
        it.toLowerCase()
      )
    )
  }

  get config(): AppRestrictions {
    return this.rootStore.appStore.config
  }

  get showHomeContentSources() {
    return this.testAction("VIEW_HOMEPAGE_CONTENT_SOURCES")
  }

  get showHomeContentTypes() {
    return this.testAction("VIEW_HOMEPAGE_CONTENT_TYPES")
  }

  get showHomeProducts() {
    return this.testAction("VIEW_HOMEPAGE_PRODUCTS")
  }

  get showHomepageSidebar() {
    return (
      this.showHomeProducts ||
      this.showHomeContentTypes ||
      this.showHomeContentSources
    )
  }

  get isProductsPageEnabled() {
    return this.testFlag("showProductsPage")
  }

  /** Build flag and instance flag must both be on before Copilot appears. */
  get showCopilot() {
    return isCopilotStudioEnabled && this.testFlag("enableCopilotStudio")
  }

  get canViewProducts() {
    return this.testAction("VIEW_PRODUCTS") && this.isProductsPageEnabled
  }

  get canAddProducts() {
    return this.testAction("CREATE_PRODUCTS")
  }

  get canDeleteProduct() {
    return this.testAction("DELETE_PRODUCT")
  }

  get canViewProduct() {
    return this.testAction("VIEW_PRODUCT")
  }

  get canUpdateProduct() {
    return this.testAction("UPDATE_PRODUCT")
  }

  get shouldOpenSolutionOnLogin() {
    return this.testFlag("openSolutionsOnLogin")
  }

  get canEditAvatar() {
    return this.testAction("CREATE_AVATAR")
  }

  get canSuggestImprovements() {
    return this.testAction("SUGGEST_IMPROVEMENTS")
  }

  get canVerifyAnswer() {
    return this.testAction("VERIFY_ANSWERS")
  }

  get isAvatarsAvailable() {
    return this.testFlag("showChannels") && this.testAction("VIEW_AVATARS")
  }

  get canAssignDataToAvatar() {
    return (
      this.testFlag("showChannels") && this.testAction("ASSIGN_DATA_AVATAR")
    )
  }

  get canViewQuestions() {
    return (
      this.testFlag("showKnowledgeAnalytics") &&
      this.testAction("VIEW_KNOWLEDGE_ANALYTICS")
    )
  }

  get canDeleteExpertQuestion() {
    return this.testAction("REMOVE_EXPERT_QUESTION")
  }

  get isAppUsageAnalyticsAvailable() {
    return (
      this.testFlag("showUsageAnalytics") &&
      this.testAction("VIEW_USAGE_ANALYTICS")
    )
  }

  get canAccessAPIKeys() {
    return this.testAction("VIEW_API_KEY")
  }

  get isAPIUsageAnalyticsAvailable() {
    return (
      this.testFlag("showAPIUsageAnalytics") &&
      this.testAction("VIEW_API_LOG_ANALYTICS")
    )
  }

  get isLatestUpdatesAvailable() {
    return this.testFlag("showLatestUpdates")
  }

  get tableViewResultIsImage() {
    return this.testFlag("showTableScreenShot")
  }

  get isImageInterpretationEnabled() {
    return this.testFlag("enableImageInterpretation")
  }

  get canEditDataSource() {
    return this.testAction("UPLOAD_DATA")
  }

  get canDeleteDataSource() {
    return this.testAction("DELETE_DATA")
  }

  get canViewDataSources() {
    return (
      this.testFlag("showDataExplorer") && this.testAction("VIEW_DATA_SOURCES")
    )
  }

  get canInviteUsers() {
    return this.testAction("CREATE_USER")
  }

  get canDeleteUser() {
    return this.testAction("DELETE_USER")
  }

  get isNeshStatusAvailable() {
    return this.testAction("VIEW_NESH_STATUS")
  }

  get canAssignAnswerToExpert() {
    return this.testFlag("showAnswerFeedback")
  }

  get isAdminPanelAvailable() {
    return this.testAction("VIEW_ADMIN_PANEL")
  }

  get isExtendedResultAvailable() {
    return !this.testFlag("disableSearchExtendedView")
  }

  get isInformalKnowledgeSectionAvailable() {
    return !this.testFlag("disableInformalKnowledgeSection")
  }

  get isExpertKnowledgeSectionAvailable() {
    return !this.testFlag("disableExpertKnowledgeSection")
  }

  /**
   * @deprecated
   */
  get isAnswersSortingByModifiedDate() {
    return this.testFlag("showSortModifiedDateOption")
  }

  get onlySuperAdminAccess() {
    return this.rootStore.userStore.isSuperAdmin
  }

  get isUsersListAvailable() {
    return this.testAction("VIEW_USERS")
  }

  get canAddAction() {
    return this.testAction("ADD_ACTION")
  }

  get canUpdateAction() {
    return this.testAction("UPDATE_ACTION")
  }

  get canAddRole() {
    return this.testAction("ADD_ROLE")
  }

  get canUpdateRole() {
    return this.testAction("UPDATE_ROLE")
  }

  get canResetPassword() {
    return this.testAction("RESET_USER_PASSWORD")
  }

  get showAIGeneratedSummaryAlert() {
    return this.testFlag("showSummaryAIAlert")
  }

  get isGlossaryAvailable() {
    return (
      this.testFlag("showGlossary") && this.testAction("VIEW_WORD_GLOSSARY")
    )
  }

  get canEditGlossary() {
    return this.testAction("UPDATE_WORD_GLOSSARY")
  }

  get isGlossaryUploadEnabled() {
    return this.testFlag("enableGlossaryUpload")
  }

  get isVoiceRecognitionEnabled() {
    return this.testFlag("enableVoiceRecognizer")
  }

  get isSearchVoiceRecognitionEnabled() {
    return this.testFlag("enableSearchVoiceRecognizer")
  }

  get canViewLeaderboard() {
    return (
      this.testFlag("enableExpertsLeaderboard") &&
      this.testAction("VIEW_EXPERTS_LEADERBOARD")
    )
  }

  get canViewExpertInsights() {
    return (
      this.testFlag("enableExpertInsights") &&
      this.testAction("VIEW_EXPERT_INSIGHT_PANEL")
    )
  }

  get canViewExpertSection() {
    return (
      this.canViewQuestions ||
      this.canViewExpertInsights ||
      this.canViewLeaderboard
    )
  }

  get canCreateExpertArticle() {
    return this.testAction("ADD_ARTICLE")
  }

  get canCreateExpertArticleInsights() {
    return this.testAction("ADD_INSIGHT")
  }

  get canUpdateExpertArticleInsights() {
    return this.testAction("UPDATE_INSIGHT")
  }

  get canDeleteExpertArticleInsights() {
    return this.testAction("DELETE_INSIGHT")
  }

  get isAppAgreementsStampAvailable() {
    return !this.testFlag("hideAppAgreementsStamp")
  }

  get canSwitchTheme() {
    return this.testFlag("allowThemeSwitch")
  }

  get isIEEEUsabillaScriptAllowed() {
    return this.testFlag("allowIEEEUsabillaScript")
  }

  get isSubscriptionPageAvailable() {
    return this.testAction("SUBSCRIPTION_MANAGEMENT_VIEW")
  }

  get isSubscriptionUpdateAvailable() {
    return this.testAction("SUBSCRIPTION_MANAGEMENT_UPDATE")
  }

  get isUserGroupsAvailable() {
    return this.testAction("VIEW_GROUP")
  }

  get canCreateUserGroups() {
    return this.testAction("CREATE_GROUP")
  }

  get canEditUserGroups() {
    return this.testAction("UPDATE_GROUP")
  }

  get canDeleteUserGroups() {
    return this.testAction("DELETE_GROUP")
  }

  get canManageUserGroupsMembership() {
    return this.testAction("MANAGE_GROUP_USERS")
  }

  get isSearchFilterAvailable() {
    return !this.testFlag("disableSearchFilters")
  }

  get isContentManagerEnabled() {
    return this.testFlag("enableKibanaConnectors")
  }

  get canConnectPublicData() {
    return this.testAction("CONNECT_PUBLIC_DATA")
  }

  get canAddManufacturer() {
    return this.testAction("ADD_MANUFACTURER")
  }

  get canViewManufacturer() {
    return this.testAction("VIEW_MANUFACTURER")
  }

  get canCreateSolution() {
    return this.testAction("CREATE_SOLUTION")
  }

  get canUpdateSolution() {
    return this.testAction("UPDATE_SOLUTION")
  }

  get canUpdateSolutionPermissions() {
    return this.testAction("UPDATE_SOLUTION_PERMISSIONS")
  }

  get hideSingleOptionAvatarPicker() {
    return this.testFlag("hideSingleOptionAvatarPicker")
  }

  get canPostToExpert() {
    return this.testAction("POST_TO_EXPERT")
  }

  get canStarRate() {
    return this.testAction("RATE_ANSWER")
  }

  /** LAB-47: access to the brand to expert routing maintenance screen. */
  get canManageBrandExperts() {
    return this.testAction("MANAGE_BRAND_EXPERTS")
  }

  get canViewContentManager() {
    return (
      this.testAction("VIEW_CONTENT_MANAGER") && this.isContentManagerEnabled
    )
  }

  get canDeleteContent() {
    return this.testAction("DELETE_CONTENT")
  }

  get canAssignContentType() {
    return this.testAction("ASSIGN_CONTENT_TYPE")
  }

  get canAddContent() {
    return this.testAction("ADD_CONTENT")
  }

  get canViewIdentifier() {
    return this.testAction("VIEW_IDENTIFIER")
  }

  get canUpdateIdentifier() {
    return this.testAction("UPDATE_IDENTIFIER")
  }

  get canCreateIdentifier() {
    return this.testAction("CREATE_IDENTIFIER")
  }

  get showSearchProducts() {
    return !this.testFlag("hideSearchSuggestedProducts")
  }

  get showSearchCompanies() {
    return !this.testFlag("hideSearchSuggestedCompanies")
  }

  get showUserProfile() {
    return this.testAction("VIEW_PROFILE")
  }

  get editUserProfile() {
    return this.testAction("UPDATE_PROFILE")
  }

  get canCreateContentType() {
    return this.testAction("CREATE_KNOWLEDGE_DATA_TYPE")
  }

  get canEditContentType() {
    return this.testAction("UPDATE_KNOWLEDGE_DATA_TYPE")
  }

  get isManualDataQuotaEnabled() {
    return this.testFlag("enableManualDataQuota")
  }

  get canStartAttributeExtraction() {
    return this.testAction("START_ATTRIBUTE_EXTRACTION")
  }

  get canTriggerProductContentTagging() {
    return this.testAction("TRIGGER_PRODUCT_CONTENT_TAGGING")
  }

  get canViewPermissions() {
    return this.testAction("view_user_permissions")
  }

  get canAddPermissions() {
    return this.testAction("create_user_permissions")
  }

  get canUpdatePermissions() {
    return this.testAction("update_user_permissions")
  }

  get isPublicContentEnabled() {
    return this.testFlag("enableTrialConnector")
  }

  get showSearchContentWarming() {
    return !this.testFlag("disableSearchContentWarning")
  }

  get canAttachAnswerDocuments() {
    return this.testFlag("allowAnswerAttachments")
  }

  get canSeeSearchWorkbookSuggestions() {
    return this.testFlag("enableSearchWorkbookSuggestions")
  }

  get isSearchWorkbookAutocompleteAvailable() {
    return this.testFlag("enableSearchWorkbookAutocomplete")
  }

  get allowWebSearch() {
    return this.testFlag("webSearch")
  }

  get allowOnlineSearch() {
    return this.testFlag("webSearch") || this.testFlag("onlineSearch")
  }

  get testAction() {
    const { userActions } = this
    if (userActions == null) return () => false
    return (action: string): boolean => {
      return isAllActionsAvailable || !!userActions?.has(action.toLowerCase())
    }
  }

  get testFlag() {
    const { config } = this
    return (param: keyof AppRestrictions): boolean =>
      (isALLFeaturesAvailable || config[param]) ?? false
  }

  get testDataSourceAccess() {
    const dataSourcesBlackList = extractSourceTypeBlackList(
      toJS(this.config),
      dataSourcesRestrictions
    ) as string[]

    return (dataSources: string | string[]) => {
      if (Array.isArray(dataSources))
        return !intersection(dataSources, dataSourcesBlackList).length
      return !dataSourcesBlackList.includes(dataSources)
    }
  }
}

export default RestrictionsStore
